<template>
  <ec-flex vertical full :vGap="0" alignItems="stretch" style="flex-grow: 1">
    <slot name="header"></slot>
    <n-data-table
      v-if="!networkError"
      ref="pcTable"
      flexHeight
      style="flex-grow: 1; min-height: 250px"
      :loading="loading"
      :columns="processedColumns"
      :data="data"
      :cascade="cascade"
      :size="size"
      :single-column="!rowDivider"
      :single-line="!columnDivider"
      :rowProps="rowProps"
      :rowKey="rowKey"
      :scrollX="scrollX"
      :expandedRowKeys="expandedRowKeys"
      @update:expandedRowKeys="onExpandedRowKeys"
      :checkedRowKeys="checkedRowKeys"
      @update:checkedRowKeys="onCheckedRowKeys"
      :summary="summary"></n-data-table>
    <div v-if="addButton.length > 0" style="width: 100%">
      <ec-button type="primary" style="width: 100%" @click="emits('add')">添加记录</ec-button>
    </div>
    <ec-flex full alignItems="center" justify="end" v-if="showPagination && !networkError" style="margin-top: 12px">
      <slot name="footer"></slot>
      <div>共 {{ pagination.itemCount }} 条</div>
      <n-pagination v-model:page="pagination.page" :pageCount="pagination.pageCount" v-model:pageSize="pagination.pageSize" @update:page="onPage" :pageSizes="pageSizes" @update:pageSize="onPageSize" :showSizePicker="showSizePicker"></n-pagination>
      <slot name="pagination"></slot>
    </ec-flex>
    <div v-if="networkError" class="flex-row-nowrap-center-center-center" style="width: 100%; padding-top: 100px">
      <n-result status="500" title="网络错误" description="服务器似乎在开小差，点击按钮重试">
        <template #footer>
          <ec-button type="primary" @click="emits('retry')">重试</ec-button>
        </template>
      </n-result>
    </div>
  </ec-flex>
</template>
<script lang="ts" setup>
import { computed, watch, ref } from "vue"
import { calScrollX } from "@/util/TableHelper"
import { DataTableCreateSummary, NDataTable } from "naive-ui"

const props = withDefaults(
  defineProps<{
    columns: Array<any>
    data: Array<any>
    loading?: boolean
    rowProps?: any
    rowKey?: any
    expandedRowKeys?: Array<string>
    checkedRowKeys?: Array<string>
    cascade?: boolean
    size?: string
    pagination?: any
    networkError?: boolean
    columnDivider?: boolean
    rowDivider?: boolean
    summary?: DataTableCreateSummary
    addButton?: string
    showTotal?: boolean
    pageSizes?: Array<number>
    /** 数据变化时是否自动滚动到顶部，默认 true */
    scrollToTopOnDataChange?: boolean
    /** 表头文字对齐方式，与列的 align 独立，默认 center */
    headTitleAlign?: "left" | "center" | "right"
  }>(),
  {
    networkError: false,
    size: "small",
    columnDivider: false,
    rowDivider: true,
    addButton: "",
    showTotal: false,
    scrollToTopOnDataChange: true,
    headTitleAlign: "center",
  },
)
const emits = defineEmits<{
  (e: "update:expandedRowKeys", expandedKeys: Array<string>): void
  (e: "update:checkedRowKeys", expandedKeys: Array<string>): void
  (e: "update:page", page: number): void
  (e: "update:pageSize", pageSize: number): void
  (e: "retry"): void
  (e: "add"): void
}>()

const pcTable = ref<InstanceType<typeof NDataTable> | null>(null)

const processedColumns = computed(() => {
  const columns = props.columns
  return columns.map((column) => ({
    ...column,
    titleAlign: column.titleAlign ?? props.headTitleAlign,
  }))
})

const scrollX = computed(() => calScrollX(processedColumns.value))

const showSizePicker = computed(() => {
  return props.pageSizes && props.pageSizes?.length > 0
})

const showPagination = computed(() => {
  return props.pagination && props.pagination.page && props.pagination.page > 0
})

const onExpandedRowKeys = (expandedKeys: Array<string>) => {
  emits("update:expandedRowKeys", expandedKeys)
}

const onCheckedRowKeys = (checkedKeys: Array<string>) => {
  emits("update:checkedRowKeys", checkedKeys)
}

const onPage = (page: number) => {
  emits("update:page", page)
}

const onPageSize = (pageSize: number) => {
  emits("update:pageSize", pageSize)
}

const downloadCsv = (option: any) => {
  pcTable.value?.downloadCsv(option)
}

watch(
  () => props.data,
  () => {
    if (props.scrollToTopOnDataChange) {
      pcTable.value?.scrollTo(0, 0)
    }
  },
)

defineExpose({
  downloadCsv,
})
</script>
<style scoped></style>
